import Candle from '@assets/icons/Candle0.svg';
import Candle0 from '@assets/icons/Candle=0.svg';
import Candle1 from '@assets/icons/Candle=1.svg';
import Candle2 from '@assets/icons/Candle=2.svg';
import Candle3 from '@assets/icons/Candle=3.svg';
import Candle4 from '@assets/icons/Candle=4.svg';
import Candle5 from '@assets/icons/Candle=5.svg';
import Candle6 from '@assets/icons/Candle=6.svg';
import Candle7 from '@assets/icons/Candle=7.svg';
import Candle8 from '@assets/icons/Candle=8.svg';
import Candle9 from '@assets/icons/Candle=9.svg';
import Candle10 from '@assets/icons/Candle=10.svg';
import Candle11 from '@assets/icons/Candle=11.svg';
import Candle12 from '@assets/icons/Candle=12.svg';
import { ICandle } from "./storyFace.types";

export const candlesSVG = [
  Candle,    // For total time = 0
  Candle0,   //  > 0 to <30 days
  Candle1,   // 30 to <60 days
  Candle2,   // 60 to <90 days
  Candle3,   // 90 to <120 days
  Candle4,   // 120 to <150 days
  Candle5,   // 150 to <180 days
  Candle6,   // 180 to <210 days
  Candle7,   // 210 to <240 days
  Candle8,   // 240 to <270 days
  Candle9,   // 270 to <300 days
  Candle10,  // 300 to <330 days
  Candle11,  // 330 to <360 days
  Candle12   // 360 days or more (capped)
];

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Computes the remaining time (in ms) using chain logic.
 * It sorts the candles by creation time and builds a "chain" where each candle extends the lifetime
 * if it falls within the current chain. When a candle is created after the current chain expires,
 * a new chain is started.
 */
function computeRemainingTimeMs(candles: ICandle[]): number {
  if (!candles || candles.length === 0) return 0;

  // Sort candles by created_at ascending.
  const sorted = candles.slice().sort(
    (a, b) =>
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );

  // Start the chain with the earliest candle.
  let currentChainStart = new Date(sorted[0].created_at).getTime();
  let currentChainExpiration =
    currentChainStart + sorted[0].candle_lifetime_days * MS_PER_DAY;

  for (let i = 1; i < sorted.length; i++) {
    const candle = sorted[i];
    const candleTime = new Date(candle.created_at).getTime();
    const candleLifetimeMs = candle.candle_lifetime_days * MS_PER_DAY;

    if (candleTime <= currentChainExpiration) {
      // Candle falls within the current chain – extend the chain.
      currentChainExpiration += candleLifetimeMs;
    } else {
      // Candle is created after the current chain expired; start a new chain.
      currentChainStart = candleTime;
      currentChainExpiration = candleTime + candleLifetimeMs;
    }
  }

  const now = Date.now();
  return Math.max(currentChainExpiration - now, 0);
}

/**
 * Returns the appropriate Candle SVG icon based on the total remaining time.
 * - If remaining time is 0, returns the base Candle.
 * - Otherwise, if remaining time is between 0 and 30 days, returns Candle0;
 *   if between 30 and 60 days, returns Candle1; and so on.
 * The icon is capped to the highest available if remaining time exceeds the highest interval.
 */
export function getCandleByValue(candles: ICandle[]): string {
  const remainingMs = computeRemainingTimeMs(candles);
  const remainingDays = remainingMs / MS_PER_DAY;

  // If no time remains, return the base Candle icon.
  if (remainingDays === 0) return candlesSVG[0];

  // Determine which 30-day interval the remaining time falls into.
  let index = Math.floor(remainingDays / 15);

  // The first interval (0 to 30 days) corresponds to candlesSVG[1] (Candle0)
  // Cap the index so it doesn't exceed the available icons.
  if (index >= candlesSVG.length - 1) {
    index = candlesSVG.length - 2;
  }

  return candlesSVG[index + 1];
}
